import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { ChatMessage } from '@app/interfaces';
import { StreamingConfigService } from '../config/streaming-config.service';
import { AuthClientService } from '../auth-client/auth-client.service';
import { AuthService } from '@craig/core';

export interface SSERawEvent {
    data: string;
    event?: string;
    id?: string;
    retry?: number;
}

/**
 * Service responsible for managing SSE connections and raw event streams.
 * This service handles the low-level SSE connection but does NOT process events.
 * Uses AuthClientService for unified auth and error handling.
 */
@Injectable()
export class SSEStreamHandlerService {
    // Raw event stream
    private readonly rawEventsSubject$ = new Subject<SSERawEvent>();
    public readonly rawEvents$ = this.rawEventsSubject$.asObservable();

    // Connection state
    private readonly isConnectedSubject$ = new BehaviorSubject<boolean>(false);
    public readonly isConnected$ = this.isConnectedSubject$.asObservable();

    // Error stream
    private readonly errorsSubject$ = new Subject<Error>();
    public readonly errors$ = this.errorsSubject$.asObservable();

    // Stream completion
    private readonly completionSubject$ = new Subject<void>();
    public readonly completion$ = this.completionSubject$.asObservable();

    // Abort controller for cancelling streams
    private abortController: AbortController | null = null;

    // Track if error was already emitted to prevent duplicates
    private errorEmitted = false;

    constructor(
        private readonly streamingConfigService: StreamingConfigService,
        private readonly authClientService: AuthClientService,
        private readonly authService: AuthService
    ) {}

    /**
     * Start SSE stream with messages
     * Uses AuthClientService for unified auth and error handling
     */
    async startStream(messages: ChatMessage[], endpoint?: string): Promise<void> {
        // Abort any existing connection
        this.abort();

        const streamEndpoint = endpoint || this.streamingConfigService.getStreamingEndpoint();
        // console.log('🚀 SSEStreamHandler - Starting stream to:', streamEndpoint);
        // console.log('🚀 SSEStreamHandler - Message count:', messages.length);

        // Reset error state for new stream
        this.errorEmitted = false;

        this.abortController = new AbortController();

        // Get user email from auth service for MLflow tracing
        const userEmail = this.authService.getUserEmail();

        // Prepare request payload following MLflow 3.0 ResponsesAgent schema
        const requestPayload: any = {
            input: messages
                .filter((msg) => msg.content && msg.content.trim().length > 0)
                .map((msg) => ({
                    role: msg.role,
                    content: msg.content
                }))
        };

        // Add user context for MLflow tracing per ResponsesAgent schema (if available)
        if (userEmail) {
            requestPayload.context = { user_id: userEmail };
        } else {
            console.warn('⚠️ SSEStreamHandler - No user email available for MLflow context');
        }

        // Get auth headers from AuthClientService - single source of truth
        const headers = this.authClientService.getAuthHeaders({
            'Content-Type': 'application/json',
            Accept: 'text/event-stream'
        });

        // console.log('🚀 SSEStreamHandler - Headers configured via AuthClientService');

        try {
            await fetchEventSource(streamEndpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(requestPayload),
                signal: this.abortController.signal,

                onopen: async (response) => {
                    if (response.ok) {
                        // console.log('🔗 SSEStreamHandler - Connection opened successfully');
                        this.isConnectedSubject$.next(true);
                    } else {
                        console.error('❌ SSEStreamHandler - Failed to open connection, status:', response.status);
                        // Let AuthClientService handle status code interpretation
                        const errorInfo = this.authClientService.handleStreamError({
                            response,
                            status: response.status
                        });
                        // Create error with status preserved so catch block doesn't lose it
                        const enhancedError = new Error(errorInfo.userMessage);
                        (enhancedError as any).status = response.status;
                        (enhancedError as any).shouldRetry = errorInfo.shouldRetry;
                        (enhancedError as any).handled = true; // Mark as already handled
                        throw enhancedError;
                    }
                },

                onmessage: (event) => {
                    const rawEvent: SSERawEvent = {
                        data: event.data,
                        event: event.event,
                        id: event.id,
                        retry: event.retry
                    };

                    // Just emit the raw event - processing happens elsewhere
                    this.rawEventsSubject$.next(rawEvent);

                    // Check for completion signal
                    if (event.data === '[DONE]' || event.data.trim() === '[DONE]') {
                        // console.log('✅ SSEStreamHandler - Stream completed with [DONE]');
                        this.handleCompletion();
                    }
                },

                onerror: (error) => {
                    console.error('❌ SSEStreamHandler - Error occurred:', error);
                    // Let AuthClientService handle error interpretation
                    const errorInfo = this.authClientService.handleStreamError(error);
                    const enhancedError = new Error(errorInfo.userMessage);
                    (enhancedError as any).shouldRetry = errorInfo.shouldRetry;
                    (enhancedError as any).handled = true; // Mark as already handled
                    this.handleError(enhancedError);
                    throw enhancedError; // Causes retry or stop based on shouldRetry
                },

                onclose: () => {
                    // console.log('🔌 SSEStreamHandler - Connection closed');
                    this.handleCompletion();
                }
            });
        } catch (error) {
            console.error('❌ SSEStreamHandler - Stream connection failed:', error);
            // Only handle if not already handled (avoid double-handling from onopen/onerror)
            if (!(error as any).handled) {
                this.handleError(error as Error);
            } else {
                // Already handled, just emit the error
                this.errorEmitted = true;
                this.isConnectedSubject$.next(false);
                this.errorsSubject$.next(error as Error);
            }
        }
    }

    /**
     * Abort the current stream
     */
    abort(): void {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
        this.isConnectedSubject$.next(false);
    }

    /**
     * Handle stream completion
     */
    private handleCompletion(): void {
        this.isConnectedSubject$.next(false);
        this.completionSubject$.next();
    }

    /**
     * Handle stream error
     * Only emits the first error to prevent duplicates
     */
    private handleError(error: Error): void {
        // Only emit error once - prevents duplicate error messages
        // onerror and catch block can both fire for the same error
        if (this.errorEmitted) {
            // console.log('⚠️ SSEStreamHandler - Ignoring duplicate error emission');
            return;
        }

        this.errorEmitted = true;
        this.isConnectedSubject$.next(false);
        this.errorsSubject$.next(error);
    }

    /**
     * Check if currently connected
     */
    get isCurrentlyConnected(): boolean {
        return this.isConnectedSubject$.value;
    }
}
